import React, { useState, useEffect, useContext } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  Check,
  Close,
  Phone,
  Schedule,
  BookmarkBorder,
  Event
} from '@mui/icons-material';
import { ApiContext } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

const CallTypificationPanel = ({
  open,
  onClose,
  onSubmit,
  onSubmitAndContinue, // New prop for preview mode
  isPreviewMode = false, // Flag to identify preview mode
  callInfo = null,
  groupId = 1, // Default to group 1 as requested
  skillDef = null, // Skill definition for logging purposes
  inline = false, // New prop to control rendering mode
  autoTypificationTimeLeft = null, // Tiempo restante para auto-tipificación en segundos
  autoTypificationMaxTime = null, // Tiempo máximo configurado en segundos
  isInGracePeriod = false, // Flag para deshabilitar botones durante período de gracia (2 seg)
  isInTracking = false // Si la llamada ya está en seguimiento
}) => {
  const { ends, agentTags } = useContext(ApiContext);
  const { user } = useAuth();
  const { canAction } = usePermissions();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTypification, setSelectedTypification] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Estados para seguimiento
  const [markForTracking, setMarkForTracking] = useState(isInTracking);
  const [trackingTags, setTrackingTags] = useState([]);
  const [trackingNote, setTrackingNote] = useState('');
  const [showTrackingOptions, setShowTrackingOptions] = useState(isInTracking);

  // Estados para agendamiento
  const [scheduleCallback, setScheduleCallback] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');
  const todayStr = new Date().toISOString().split('T')[0];
  const nowStr = new Date().toTimeString().slice(0, 5);

  // Etiquetas personalizadas del agente
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Load typification tree
  const loadTree = async () => {
    try {
      setLoading(true);

      console.log('[CallTypificationPanel] Iniciando carga de finales', {
        groupId,
        skillDef,
        open
      });

      // Intentar obtener desde caché primero (sessionStorage)
      const response = await ends.getTree(groupId, {
        cache: true,
        skillDef: skillDef,
        include_inactive: true
      });

      if (response.success) {
        const data = response.data || [];
        setTree(data);

        console.log('[CallTypificationPanel] Finales cargados correctamente', {
          groupId,
          skillDef,
          itemsCount: data.length,
          fromCache: response.fromCache,
          source: response.fromCache ? 'sessionStorage (CACHÉ)' : 'SERVIDOR'
        });

        // Auto-expand first level
        const firstLevelIds = data
          .filter(node => node.level === 1)
          .map(node => node.id.toString());
        setExpanded(firstLevelIds);
      } else {
        console.error('[CallTypificationPanel] Error cargando finales:', {
          groupId,
          skillDef,
          response
        });
      }
    } catch (error) {
      console.error('[CallTypificationPanel] Excepción cargando finales:', {
        groupId,
        skillDef,
        error: error.message,
        stack: error.stack
      });
    } finally {
      setLoading(false);
    }
  };

    // Cargar etiquetas personalizadas del agente
    const loadAgentTags = async () => {
      try {
        setLoadingTags(true);

        // Obtener agentId del usuario actual desde el contexto de autenticación
        const agentId = user?.username || user?.id;
        console.log('[CALL TYPIFICATION] Cargando etiquetas para agente:', agentId);

        if (!agentId) {
          console.warn('[CALL TYPIFICATION] No se pudo obtener agentId');
          return;
        }

        const result = await agentTags.getAll(agentId);
        console.log('[CALL TYPIFICATION] Respuesta de etiquetas:', result, result.fromCache ? '(CACHE)' : '(SERVER)');

        if (result.success) {
          console.log('[CALL TYPIFICATION] Etiquetas cargadas:', result.data);
          setAvailableTags(result.data || []);
        } else {
          console.error('[CALL TYPIFICATION] Error en respuesta:', result);
        }
      } catch (error) {
        console.error('[CALL TYPIFICATION] Error cargando etiquetas:', error);
      } finally {
        setLoadingTags(false);
      }
    };

  useEffect(() => {
    if (open && groupId) {
      loadTree();
      loadAgentTags();
      setSelectedTypification(null);
    }
  }, [open, groupId]);

  // Handle tree node toggle
  const handleToggle = (nodeId) => {
    setExpanded(prev =>
      prev.includes(nodeId)
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  // Handle tree node selection
  const handleSelect = (node) => {
    if (node && node.is_final) {
      setSelectedTypification(node);
    }
  };

  // Build complete typification path
  const buildTypificationPath = (selectedNode) => {
    if (!selectedNode || !tree) return '';

    const findNodePath = (nodes, targetId, currentPath = []) => {
      for (const node of nodes) {
        const newPath = [...currentPath, node.name];

        if (node.id === targetId) {
          return newPath;
        }

        if (node.children && node.children.length > 0) {
          const result = findNodePath(node.children, targetId, newPath);
          if (result) return result;
        }
      }
      return null;
    };

    const path = findNodePath(tree, selectedNode.id);
    return path ? path[0] : selectedNode.name;
  };

  // Handle typification submission with tracking support
  const handleSubmit = async () => {
    await handleSubmitWithTracking(false);
  };

  // Handle typification and continue (for preview mode)
  const handleSubmitAndContinue = async () => {
    await handleSubmitWithTracking(true);
  };

  // Nueva función para manejar submit con tracking
  const handleSubmitWithTracking = async (continueCall) => {
    if (!selectedTypification) return;

    try {
      setSubmitting(true);
      // Validar fecha/hora futura antes de enviar
      if (scheduleCallback) {
        if (!scheduledDate || !scheduledTime) {
          setSubmitting(false);
          alert('Selecciona fecha y hora para el callback');
          return;
        }
        const combined = `${scheduledDate}T${scheduledTime}:00`;
        const parsed = new Date(combined);
        const now = new Date();
        if (Number.isNaN(parsed.getTime()) || parsed <= now) {
          setSubmitting(false);
          alert('La fecha y hora del callback debe ser futura');
          return;
        }
      }

      // Build the complete typification data
      const typificationData = {
        ...selectedTypification,
        path: buildTypificationPath(selectedTypification)
      };

      console.log('[CALL TYPIFICATION PANEL] Estado de tracking:', {
        markForTracking,
        trackingTags,
        trackingNote,
        scheduleCallback,
        scheduledDate,
        scheduledTime,
        continueCall
      });

      // Datos de seguimiento si está marcado
      const trackingData = markForTracking ? {
        tags: trackingTags,
        note: trackingNote.trim() || null,
        // Datos de agendamiento si se marcó
        schedule: scheduleCallback && scheduledDate && scheduledTime ? {
          scheduled_date: `${scheduledDate}T${scheduledTime}:00`,
          schedule_note: scheduleNote.trim() || null
        } : null
      } : null;

      console.log('[CALL TYPIFICATION PANEL] TrackingData generado:', trackingData);
      console.log('[CALL TYPIFICATION PANEL] TypificationData:', typificationData);

      if (continueCall && onSubmitAndContinue) {
        console.log('[CALL TYPIFICATION PANEL] Llamando onSubmitAndContinue con trackingData');
        await onSubmitAndContinue(typificationData, trackingData);
      } else {
        console.log('[CALL TYPIFICATION PANEL] Llamando onSubmit con trackingData');
        await onSubmit(typificationData, trackingData);
      }

      onClose();
    } catch (error) {
      console.error('[CALL TYPIFICATION PANEL] Error submitting typification:', error);
      console.error('[CALL TYPIFICATION PANEL] Error completo:', error.stack);
    } finally {
      setSubmitting(false);
    }
  };

  // Render tree item recursively
  const renderTreeItems = (nodes, level = 0) => {
    return nodes.map((node) => {
      const nodeId = node.id.toString();
      const isExpanded = expanded.includes(nodeId);
      const isSelected = selectedTypification?.id === node.id;
      const hasChildren = node.children && node.children.length > 0;

      return (
        <React.Fragment key={node.id}>
          <ListItem
            disablePadding
            sx={{
              display: 'block',
              pl: level * 2
            }}
          >
            <ListItemButton
              onClick={() => {
                if (isInGracePeriod) return; // Bloquear clicks durante período de gracia
                if (hasChildren) {
                  handleToggle(nodeId);
                }
                if (node.is_final) {
                  handleSelect(node);
                }
              }}
              selected={isSelected}
              disabled={isInGracePeriod}
              sx={{
                minHeight: 32,
                py: 0.5,
                pl: 1,
                borderRadius: 1,
                mx: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light'
                  }
                },
                cursor: isInGracePeriod ? 'not-allowed' : (node.is_final || hasChildren ? 'pointer' : 'default'),
                opacity: isInGracePeriod ? 0.5 : 1
              }}
            >
              {hasChildren && (
                <ListItemIcon sx={{ minWidth: 24 }}>
                  {isExpanded ? (
                    <ExpandMore sx={{ fontSize: 20 }} />
                  ) : (
                    <ChevronRight sx={{ fontSize: 20 }} />
                  )}
                </ListItemIcon>
              )}

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: node.is_final ? 'primary.main' : 'text.primary',
                        fontWeight: node.is_final ? 'bold' : 'normal'
                      }}
                    >
                      {node.name}
                    </Typography>
                    {node.code && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({node.code})
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </ListItemButton>
          </ListItem>

          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderTreeItems(node.children, level + 1)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };

  if (!open) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        ...(inline ? {
          width: 320,
          height: '100%',
          borderLeft: '1px solid #e0e0e0'
        } : {
          position: 'fixed',
          top: 64, // AppBar height
          right: 0,
          width: 400,
          height: 'calc(100vh - 64px)',
          zIndex: 1200,
          borderRadius: '8px 0 0 0'
        }),
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: inline ? '#f5f5f5' : 'inherit'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Phone sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant={inline ? "subtitle1" : "h6"} sx={{ fontWeight: 600 }}>
              Tipificar Llamada
            </Typography>
          </Box>
          <Tooltip title="Cerrar">
            <IconButton onClick={onClose} size="small" sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#e0e0e0'
              }
            }}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Call Info */}
        {callInfo && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Información de la llamada
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Schedule sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2">
                Duración: {callInfo.duration || 'No disponible'}
              </Typography>
            </Box>
            {callInfo.number && (
              <Typography variant="body2" color="text.secondary">
                Número: {callInfo.number}
              </Typography>
            )}
          </Box>
        )}

        {/* Auto-Typification Timer */}
        {autoTypificationTimeLeft !== null && autoTypificationTimeLeft > 0 && autoTypificationMaxTime && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
              px: 1
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#ed6c02' }}>
                Auto-tipificación en:
              </Typography>
              <Chip
                label={`${autoTypificationTimeLeft} s`}
                color="warning"
                size="small"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  minWidth: 50,
                  height: 28
                }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={(autoTypificationTimeLeft / autoTypificationMaxTime) * 100}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: 'rgba(237, 108, 2, 0.15)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ed6c02',
                  borderRadius: 1,
                  transition: 'transform 1s linear'
                }
              }}
            />
          </Box>
        )}
      </Box>

      {/* Body (scrollable) */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : tree.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">
                No hay tipificaciones disponibles para este grupo.
              </Alert>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Selecciona una tipificación:
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
                <List component="nav" sx={{ pb: 2 }}>
                  {renderTreeItems(tree)}
                </List>
              </Box>
            </>
          )}
        </Box>

        {/* Tracking Options Section - NEW */}
        {!isInTracking && canAction('allow_tracking_typification') && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={markForTracking}
                  onChange={(e) => {
                    setMarkForTracking(e.target.checked);
                    setShowTrackingOptions(e.target.checked);
                  }}
                  disabled={isInGracePeriod}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BookmarkBorder sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Marcar para seguimiento
                  </Typography>
                </Box>
              }
            />

            <Collapse in={showTrackingOptions} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.5, pl: 4 }}>
                {/* Etiquetas */}
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Etiquetas:
                </Typography>
                {loadingTags ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : availableTags.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No tienes etiquetas configuradas. Ve a "Mis Etiquetas" para crear etiquetas personalizadas.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {availableTags.map(tag => (
                      <Chip
                        key={tag.id}
                        label={tag.tag_name}
                        size="small"
                        onClick={() => {
                          setTrackingTags(prev =>
                            prev.includes(tag.tag_name)
                              ? prev.filter(t => t !== tag.tag_name)
                              : [...prev, tag.tag_name]
                          );
                        }}
                        disabled={isInGracePeriod}
                        sx={{
                          fontSize: '0.7rem',
                          height: 24,
                          backgroundColor: trackingTags.includes(tag.tag_name) ? tag.tag_color : 'transparent',
                          color: trackingTags.includes(tag.tag_name) ? 'white' : 'inherit',
                          borderColor: tag.tag_color,
                          borderWidth: 2,
                          '&:hover': {
                            backgroundColor: trackingTags.includes(tag.tag_name) ? tag.tag_color : `${tag.tag_color}20`,
                          }
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}

                {/* Nota inicial */}
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  label="Nota (opcional)"
                  placeholder="Agregar nota de seguimiento..."
                  value={trackingNote}
                  onChange={(e) => setTrackingNote(e.target.value)}
                  disabled={isInGracePeriod}
                  sx={{
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.85rem'
                    }
                  }}
                />
            </Box>
          </Collapse>

          {/* Agendamiento de callback */}
          {markForTracking && canAction('allow_callback_typification') && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scheduleCallback}
                    onChange={(e) => setScheduleCallback(e.target.checked)}
                    disabled={isInGracePeriod}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Event sx={{ mr: 0.5, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Agendar callback
                    </Typography>
                  </Box>
                }
              />

              <Collapse in={scheduleCallback} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 1.5, pl: 4 }}>
                  <TextField
                    type="date"
                    size="small"
                    label="Fecha"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    disabled={isInGracePeriod}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: todayStr
                    }}
                    fullWidth
                    sx={{ mb: 1, fontSize: '0.8rem' }}
                  />
                  <TextField
                    type="time"
                    size="small"
                    label="Hora"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    disabled={isInGracePeriod}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: scheduledDate === todayStr ? nowStr : undefined
                    }}
                    fullWidth
                    sx={{ mb: 1, fontSize: '0.8rem' }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Nota de agendamiento (opcional)"
                    placeholder="Motivo del callback..."
                    value={scheduleNote}
                    onChange={(e) => setScheduleNote(e.target.value)}
                    disabled={isInGracePeriod}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem'
                      }
                    }}
                  />
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      )}

        {/* Selected Typification Info */}
        {selectedTypification && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              Tipificación Seleccionada:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {selectedTypification.name}
            </Typography>
            {selectedTypification.code && (
              <Typography variant="caption" color="text.secondary">
                Código: {selectedTypification.code}
              </Typography>
            )}
          </Box>
        )}

        {/* Footer Actions */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: isPreviewMode ? 'column' : 'row' }}>
            {isPreviewMode ? (
              <>
                {/* Preview mode: Show both buttons */}
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!selectedTypification || submitting || isInGracePeriod}
                  startIcon={submitting ? <CircularProgress size={16} /> : <Check />}
                  fullWidth
                >
                  {submitting ? 'Enviando...' : 'Tipificar y finalizar'}
                </Button>
                {canAction('allow_redial_typification') && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSubmitAndContinue}
                  disabled={!selectedTypification || submitting || isInGracePeriod}
                  startIcon={submitting ? <CircularProgress size={16} /> : <Phone />}
                  fullWidth
                >
                  {submitting ? 'Enviando...' : 'Tipificar y volver a llamar'}
                </Button>
                )}
              </>
            ) : (
              <>
                {/* Normal mode: Original buttons */}
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!selectedTypification || submitting || isInGracePeriod}
                  startIcon={submitting ? <CircularProgress size={16} /> : <Check />}
                  fullWidth
                >
                  {submitting ? 'Enviando...' : 'Tipificar y finalizar'}
                </Button>
              </>
            )}
          </Box>

          {!selectedTypification && !isInGracePeriod && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Selecciona una tipificación final para continuar
            </Typography>
          )}
          {isInGracePeriod && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
              Procesando auto-tipificación...
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default CallTypificationPanel;
